"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useMemo } from "react";
import Link from "next/link";
import { ChevronDown, Building2, LayoutGrid, Package, FileText, User, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { strings } from "@/lib/strings";
import { routes } from "@/lib/routes";
import { useOrganization } from "@/lib/organizations/hooks";
import { isOwnerMembership } from "@/lib/organizations/types";
import { ORG_NAV_PRIMARY, ORG_NAV_SECONDARY } from "@/components/organizations/org-nav-config";

// ============================================================================
// Shared: activity-based nav items for non-org users
// ============================================================================

/**
 * Builds the list of visible nav items based on activity counts.
 * Items only appear once the user has data for them.
 * When counts haven't loaded yet (null), show all items so the
 * menu is never empty during the brief loading window.
 */
function useVisibleNavItems() {
  const $ = _c(7);
  const {
    activityCounts
  } = useAuth();
  const t = strings;
  const countsLoaded = activityCounts !== null;
  const t0 = !countsLoaded || activityCounts.offerings > 0;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      key: "offerings",
      href: routes.offerings.list,
      label: strings.nav.myOfferings,
      description: strings.nav.myOfferingsDesc,
      icon: Package,
      hasData: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !countsLoaded || activityCounts.requests > 0;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      key: "requests",
      href: routes.requests.list,
      label: strings.nav.requests,
      description: strings.nav.myRequestsDesc,
      icon: FileText,
      hasData: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    const allItems = [t1, t3];
    t4 = allItems.filter(_temp);
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

// ============================================================================
// Desktop: AccountMenu dropdown
// ============================================================================

/**
 * Unified account dropdown for all authenticated users.
 *
 * - Org users see: "Mein Unternehmen" trigger → org nav items + Profile + Logout
 * - Non-org users see: "Mein Bereich" trigger → activity-based items + Profile + Logout
 *
 * Co-located so changes to one variant are visible alongside the other.
 */
function _temp(item) {
  return item.hasData;
}
export function AccountMenu(t0) {
  const $ = _c(37);
  const {
    className
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const {
    signOut,
    openProfileModal
  } = useAuth();
  const {
    isOrgUser,
    organization
  } = useOrganization();
  const nonOrgNavItems = useVisibleNavItems();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleClickOutside = function handleClickOutside(event) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handleEscape = function handleEscape(event_0) {
        if (event_0.key === "Escape") {
          setIsOpen(false);
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  const TriggerIcon = isOrgUser ? Building2 : LayoutGrid;
  const triggerLabel = isOrgUser ? strings.nav.myCompany : strings.nav.myArea;
  let t5;
  if ($[4] !== className) {
    t5 = cn("relative", className);
    $[4] = className;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isOpen) {
    t6 = () => setIsOpen(!isOpen);
    $[6] = isOpen;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = isOpen ? "text-secondary border-secondary" : "border-transparent text-primary hover:text-secondary";
  let t8;
  if ($[8] !== t7) {
    t8 = cn("flex items-center gap-2", "text-sm font-semibold tracking-wide", "border-b-2 pb-0.5 transition-colors duration-200", t7);
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== TriggerIcon) {
    t9 = <TriggerIcon className="w-4 h-4" />;
    $[10] = TriggerIcon;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== triggerLabel) {
    t10 = <span>{triggerLabel}</span>;
    $[12] = triggerLabel;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = isOpen && "rotate-180";
  let t12;
  if ($[14] !== t11) {
    t12 = cn("w-3.5 h-3.5 transition-transform duration-200", t11);
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12) {
    t13 = <ChevronDown className={t12} />;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== isOpen || $[19] !== t10 || $[20] !== t13 || $[21] !== t6 || $[22] !== t8 || $[23] !== t9 || $[24] !== triggerLabel) {
    t14 = <button onClick={t6} className={t8} aria-expanded={isOpen} aria-haspopup="menu" aria-label={triggerLabel} data-testid="account-menu-trigger">{t9}{t10}{t13}</button>;
    $[18] = isOpen;
    $[19] = t10;
    $[20] = t13;
    $[21] = t6;
    $[22] = t8;
    $[23] = t9;
    $[24] = triggerLabel;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== isOpen || $[27] !== isOrgUser || $[28] !== nonOrgNavItems || $[29] !== openProfileModal || $[30] !== organization || $[31] !== signOut) {
    t15 = isOpen && <div className="absolute top-full right-0 mt-2 w-64 rounded-2xl eco-glow backdrop-blur-xl bg-surface/95 py-2 z-[100] animate-in fade-in slide-in-from-top-2 duration-150" role="menu" aria-orientation="vertical" data-testid="account-menu-dropdown"><div className="py-1 px-2"><Link href="/dashboard" onClick={() => setIsOpen(false)} className="group flex items-center gap-3 px-3 py-2.5 text-sm font-bold text-primary hover:bg-primary/5 rounded-xl transition-colors" role="menuitem"><LayoutGrid className="w-4 h-4 text-secondary group-hover:text-accent transition-colors" /><span>Dashboard</span></Link></div><div className="border-t border-outline-variant/20" />{isOrgUser && <><div className="px-4 py-2 font-label text-xs uppercase tracking-[0.2em] text-secondary font-bold">{organization?.name || "Unternehmen"}</div><div className="py-1 px-2">{ORG_NAV_PRIMARY.filter(item => !item.ownerOnly || isOwnerMembership(organization)).map(item_0 => {
            const Icon = item_0.icon;
            return <Link key={item_0.id} href={item_0.href} onClick={() => setIsOpen(false)} className="group flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors" role="menuitem"><Icon className="w-4 h-4 text-secondary group-hover:text-accent transition-colors" /><span>{item_0.label}</span></Link>;
          })}<div className="my-1 border-t border-outline-variant/20" role="separator" />{ORG_NAV_SECONDARY.filter(item_1 => !item_1.ownerOnly || isOwnerMembership(organization)).map(item_2 => {
            const Icon_0 = item_2.icon;
            return <Link key={item_2.id} href={item_2.href} onClick={() => setIsOpen(false)} className="group flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors" role="menuitem"><Icon_0 className="w-4 h-4 text-secondary group-hover:text-accent transition-colors" /><span>{item_2.label}</span></Link>;
          })}</div></>}{!isOrgUser && nonOrgNavItems.length > 0 && <div className="py-1 px-2">{nonOrgNavItems.map(item_3 => <Link key={item_3.key} href={item_3.href} onClick={() => setIsOpen(false)} className="group flex items-start gap-3 px-3 py-2.5 text-sm hover:bg-primary/5 rounded-xl transition-colors" role="menuitem"><item_3.icon className="w-4 h-4 text-secondary group-hover:text-accent mt-0.5 shrink-0 transition-colors" /><div className="min-w-0"><div className="font-medium text-primary">{item_3.label}</div><div className="text-xs text-foreground-muted mt-0.5">{item_3.description}</div></div></Link>)}</div>}<div className="border-t border-outline-variant/20" /><div className="py-1 px-2"><button onClick={() => {
          openProfileModal();
          setIsOpen(false);
        }} className="group flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors w-full text-left" role="menuitem"><User className="w-4 h-4 text-secondary group-hover:text-accent transition-colors" /><span>{strings.nav.myProfile}</span></button><button onClick={() => {
          signOut();
          setIsOpen(false);
        }} className="group flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors w-full text-left" role="menuitem"><LogOut className="w-4 h-4 text-secondary group-hover:text-accent transition-colors" /><span>{strings.auth.logout}</span></button></div></div>;
    $[26] = isOpen;
    $[27] = isOrgUser;
    $[28] = nonOrgNavItems;
    $[29] = openProfileModal;
    $[30] = organization;
    $[31] = signOut;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15 || $[35] !== t5) {
    t16 = <div ref={dropdownRef} className={t5}>{t14}{t15}</div>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t5;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}

// ============================================================================
// Mobile: AccountMobileMenu
// ============================================================================

/**
 * Mobile version of the account menu. Renders as a list of buttons/links.
 *
 * - Org users see: company name header + org nav items + Profile + Logout
 * - Non-org users see: activity-based items + Profile + Logout
 */
export function AccountMobileMenu(t0) {
  const $ = _c(29);
  const {
    onItemClick
  } = t0;
  const {
    signOut,
    openProfileModal
  } = useAuth();
  const {
    isOrgUser,
    organization
  } = useOrganization();
  const nonOrgNavItems = useVisibleNavItems();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...ORG_NAV_PRIMARY, ...ORG_NAV_SECONDARY];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== organization) {
    t2 = t1.filter(item => !item.ownerOnly || isOwnerMembership(organization));
    $[1] = organization;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const ORG_NAV_ITEMS = t2;
  const displayName = organization?.name ? organization.name.length > 20 ? organization.name.slice(0, 20) + "\u2026" : organization.name : "Unternehmen";
  let t3;
  if ($[3] !== ORG_NAV_ITEMS || $[4] !== displayName || $[5] !== isOrgUser || $[6] !== onItemClick) {
    t3 = isOrgUser && <div className="space-y-2" data-testid="account-mobile-menu-org"><div className="flex items-center gap-2 px-4 py-2 font-label text-xs uppercase tracking-[0.2em] text-secondary font-bold"><Building2 className="w-4 h-4" /><span>{displayName}</span></div>{ORG_NAV_ITEMS.map((item_0, index) => {
        const Icon = item_0.icon;
        const isFirst = index === 0;
        return <Link key={item_0.id} href={item_0.href} onClick={onItemClick} className={cn("flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium rounded-xl transition-colors", isFirst ? "text-white bg-primary hover:bg-primary/90" : "text-primary border border-outline-variant/20 hover:bg-primary/5")}><Icon className="w-4 h-4" />{item_0.label}</Link>;
      })}</div>;
    $[3] = ORG_NAV_ITEMS;
    $[4] = displayName;
    $[5] = isOrgUser;
    $[6] = onItemClick;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== isOrgUser || $[9] !== nonOrgNavItems || $[10] !== onItemClick) {
    t4 = !isOrgUser && nonOrgNavItems.map(item_1 => <Link key={item_1.key} href={item_1.href} onClick={onItemClick} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-primary border border-outline-variant/20 hover:bg-primary/5 rounded-xl transition-colors"><item_1.icon className="w-4 h-4 shrink-0" /><div className="min-w-0"><div>{item_1.label}</div><div className="text-xs text-foreground-muted font-normal">{item_1.description}</div></div></Link>);
    $[8] = isOrgUser;
    $[9] = nonOrgNavItems;
    $[10] = onItemClick;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onItemClick || $[13] !== openProfileModal) {
    t5 = () => {
      openProfileModal();
      onItemClick?.();
    };
    $[12] = onItemClick;
    $[13] = openProfileModal;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <User className="w-4 h-4" />;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5) {
    t7 = <button onClick={t5} className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-primary border border-outline-variant/20 hover:bg-primary/5 rounded-xl transition-colors">{t6}{strings.nav.myProfile}</button>;
    $[16] = t5;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== onItemClick || $[19] !== signOut) {
    t8 = () => {
      signOut();
      onItemClick?.();
    };
    $[18] = onItemClick;
    $[19] = signOut;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LogOut className="w-4 h-4" />;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8) {
    t10 = <button onClick={t8} className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-primary border border-outline-variant/20 hover:bg-primary/5 rounded-xl transition-colors">{t9}{strings.auth.logout}</button>;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t3 || $[26] !== t4 || $[27] !== t7) {
    t11 = <>{t3}{t4}{t7}{t10}</>;
    $[24] = t10;
    $[25] = t3;
    $[26] = t4;
    $[27] = t7;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}